import type { WorksheetOutputV1 } from '@/sdk';

export type FontSize = 'small' | 'medium' | 'large';

export type ChatData = {
  messages: ChatMessage[];
  origin?: AssistantOrigin;
} & Record<string, any>;

export type ThinkingSectionStatus = 'running' | 'completed' | 'error';

// New types for thinking sections
export type ThinkingSectionItemType = 'tool_call' | 'code_block' | 'context_retrieval';

export interface ThinkingSectionItem {
  item_id: string; // Primary identifier for items - required
  type?: ThinkingSectionItemType; // Optional - can be empty for placeholders, filled in later
  name?: string; // Optional - can be empty for placeholders, filled in later
  description?: string; // Optional - can be empty for placeholders, filled in later
  content?: string; // For code blocks or detailed content
  status: ThinkingSectionStatus;
  startTime?: number;
  endTime?: number;
  duration?: number; // Pre-calculated duration in milliseconds
  metadata?: Record<string, any>; // For additional tool-specific data
}

export interface ThinkingSection {
  id: string;
  title: string;
  status: ThinkingSectionStatus;
  items: ThinkingSectionItem[];
  startTime: number;
  endTime?: number;
  duration?: number; // Pre-calculated duration in milliseconds
  isCollapsed?: boolean;
}

export type ChatMessage = {
  role: 'bot' | 'user';
  dialog: string;
  id: string;
  chatId?: string;
  agentType?: string;
  last?: boolean;
  agent?: string;
  origin?: AssistantOrigin;
  originDetails?: AssistantOriginDetails;
  stages?: Record<string, string | string[] | object>;
  components?: string[];
  prompt_classification?: string;
  selectedDocument?: SelectedDocument;
  thinkingSections?: ThinkingSection[]; // New field for thinking sections
};

export const CONTEXT_BUTTON_AGENTS = ['aq'];

export type AgentType = 'formula' | 'general' | 'code' | 'actions' | 'aq' | 'tome';
export type AnswerStyle = 'minimal' | 'moderate';
export type ReasoningStyle = 'enabled' | 'disabled';

export const assistantLocations = {
  homescreen: 'Home Screen',
  workbench: 'Workbench',
  organizer: 'Organizer',
  vantage: 'Vantage',
  datalab: 'Data Lab',
} as const;

export type AssistantOrigin = keyof typeof assistantLocations | 'logs';
export type AssistantOriginDetails = { label: string; url: string };

/**
 * DO NOT ACCESS THIS DIRECTLY. Use `genAIAgents()`  or getDataLabAiAgents() instead.
 */
export const agents: Agent[] = [
  {
    name: 'General',
    color: '#39516b',
    darkColor: '#54749a',
    key: 'general',
    icon: 'fc-genai-chat',
    description: 'AI_ASSISTANT.AGENT_INTRO_GENERAL',
  },
  {
    name: 'Formula',
    color: '#427c63',
    key: 'formula',
    icon: 'fc-formula-ai',
    description: 'AI_ASSISTANT.AGENT_INTRO_FORMULA',
  },
  {
    name: 'Data Lab',
    color: '#f26722',
    key: 'code',
    icon: 'fc-datalab-ai',
    description: 'AI_ASSISTANT.AGENT_INTRO_DATA_LAB',
  },
  {
    name: 'Actions',
    color: '#0491c2',
    key: 'actions',
    icon: 'fc-workbook-ai',
    description: 'AI_ASSISTANT.AGENT_INTRO_ACTIONS',
  },
  {
    name: 'Agent Q',
    color: '#ff2249',
    key: 'aq',
    icon: 'fc-workbook-ai',
    description: 'AI_ASSISTANT.AGENT_INTRO_VANTAGE',
  },
  {
    name: 'TΩme',
    color: '#4f596b',
    key: 'tome',
    icon: 'fa-book',
    description: 'AI_ASSISTANT.AGENT_INTRO_TOME',
    commandName: 'tome',
  },
];

export interface Agent {
  name: string;
  color: string;
  darkColor?: string;
  key: AgentType;
  icon: string;
  description: string;
  commandName?: string;
}

export type LevelOfSatisfaction = 'notAtAll' | 'notReally' | 'neutral' | 'satisfied' | 'verySatisfied';
type ProblemType =
  | 'wrong'
  | 'tooComplex'
  | 'other'
  | 'unclear'
  | 'concise'
  | 'accurate'
  | 'detailed'
  | 'moreConcise'
  | 'moreDetail'
  | 'moreAccuracy'
  | 'examples';
export type AnswerType = {
  problemKey: ProblemType;
  displayKey: string;
  correctionSuggestion?: string;
  correctionPrompt?: string;
  showInput?: boolean;
};
export type SatisfactionEntry = {
  icon: string;
  satisfaction: LevelOfSatisfaction;
  title: string;
  text?: string;
  options?: AnswerType[];
};

// To ADD feature flag for this
export const getDataLabAiAgents = () => {
  return 'general,formula,code';
};

export interface HistoryEntryType {
  chatId: string;
  origin?: AssistantOrigin;
  name: string;
  sharedBy?: string;
  shared: boolean;
  updatedAt?: string;
  favorite: boolean;
}

export interface GroupedHistoryEntryType {
  favorites: HistoryEntryType[];
  shared_with_me: HistoryEntryType[];
  today: HistoryEntryType[];
  yesterday: HistoryEntryType[];
  this_week: HistoryEntryType[];
  last_week: HistoryEntryType[];
  this_month: HistoryEntryType[];
  last_month: HistoryEntryType[];
  older: HistoryEntryType[];
}

export enum MagiqPromptType {
  IMPACT_REPORT_WORKBENCH = 'impact_report_workbench',
  IMPACT_REPORT_ORGANIZER = 'impact_report_organizer',
  SEEQ_LOG_SUMMARY = 'seeq_log_summary',
  IMPACT_REPORT_VANTAGE = 'impact_report_vantage',
}

export enum PromptCategory {
  FindAndCleanseData = 'Find & Cleanse Data',
  EventsAndPeriodsOfInterest = 'Events & Periods of Interest',
  AnalyzeAndQuantify = 'Analyze & Quantify',
  VisualizeAndGraph = 'Visualize & Graph',
  ModelAndPredict = 'Model & Predict',
  ScaleAnalytics = 'Scale Analytics',
  DocumentAndShare = 'Document & Share',
  ScriptAndCode = 'Script & Code',
  AdminAndDataManagement = 'Admin & Data Management',
  ReferenceYourDocs = 'Reference Your Docs',
}

export interface PromptEntry {
  title: string;
  category: PromptCategory;
  prompt: string;
  agentType: AgentType;
}

export interface DocumentItem {
  id: string;
  type: string;
  name: string;
  createdAt: string;
  updatedAt: string;
  isPinned: boolean;
  owner: {
    id: string;
    name: string;
    type: string;
    username: string;
    email: string;
  };
  effectivePermissions: {
    read: boolean;
    write: boolean;
    manage: boolean;
  };
  isArchived: boolean;
}

export interface DocumentsResponse {
  content: DocumentItem[];
  totalElements: number;
  totalPages: number;
  size: number;
  number: number;
}

export interface WorksheetsResponse {
  worksheets: WorksheetOutputV1[];
  offset: number;
  limit: number;
}

export interface DocumentWithWorksheets extends DocumentItem {
  worksheets?: WorksheetOutputV1[];
}

export interface SelectedDocument {
  id: string;
  name: string;
  type: string;
  worksheetId?: string;
  worksheetName?: string;
  parentId?: string; // For worksheet's parent document
}

// Document type priority for sorting
export const DOCUMENT_TYPE_PRIORITY: Record<string, number> = {
  Topic: 1,
  Vantage: 2,
  Analysis: 3,
};
